// Hook that fetches the feed and manages view-mode persistence.
// View mode ("feed" | "editorial") is persisted to localStorage so it
// survives page refreshes. Key: "hub:feed:view"
import { useCallback, useEffect, useRef, useState } from "react";
import { fetchFeed } from "../api";
import type { FeedListResponse, FeedQueryParams } from "../api";

export type FeedView = "feed" | "editorial";

const VIEW_STORAGE_KEY = "hub:feed:view"; // [STUB] production may migrate to user preference API

function loadView(): FeedView {
  try {
    const raw = localStorage.getItem(VIEW_STORAGE_KEY);
    if (raw === "feed" || raw === "editorial") return raw;
  } catch {
    // localStorage unavailable (SSR, private browsing, etc.)
  }
  return "feed";
}

function saveView(view: FeedView): void {
  try {
    localStorage.setItem(VIEW_STORAGE_KEY, view);
  } catch {
    // ignore storage errors
  }
}

interface UseFeedResult {
  data: FeedListResponse | null;
  loading: boolean;
  error: Error | null;
  view: FeedView;
  setView: (v: FeedView) => void;
  refetch: () => void;
}

export function useFeed(params?: FeedQueryParams): UseFeedResult {
  const [data, setData] = useState<FeedListResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [view, setViewState] = useState<FeedView>(loadView);

  const setView = useCallback((v: FeedView) => {
    saveView(v);
    setViewState(v);
  }, []);

  // Monotonic request id: the top-bar search fires a fetch per keystroke, so a
  // slower earlier response could otherwise overwrite the latest query's
  // results. Only the most recent request is allowed to commit state.
  const requestIdRef = useRef(0);

  const load = useCallback(() => {
    const requestId = ++requestIdRef.current;
    setLoading(true);
    setError(null);
    fetchFeed(params)
      .then((result) => {
        if (requestId === requestIdRef.current) setData(result);
      })
      .catch((e: unknown) => {
        if (requestId === requestIdRef.current) {
          setError(e instanceof Error ? e : new Error(String(e)));
        }
      })
      .finally(() => {
        if (requestId === requestIdRef.current) setLoading(false);
      });
  }, [params?.division, params?.sort, params?.tag, params?.q]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    load();
  }, [load]);

  return { data, loading, error, view, setView, refetch: load };
}
